import {
  ForwardRefRenderFunction,
  forwardRef,
  RefObject,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {requestContext} from '../../../../../../common/requestContext';
import {usePrevious} from '../../../../../../common/shared/hooks/usePrevious';
import {getDataset} from '../../../../../../common/shared/utils/getDataset';
import {twMerge} from '../../../../../../common/twMerge';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import {useWindowEventBlockerRef} from '../../../../../../common/useWindowEventBlockerRef.hook';
import {showViewerImagePopup} from '../../../../../../imagePopup/imagePopupHandler';
import {useDrawHtmlInfoHandler} from '../hook/useDrawHtmlInfoHandler.hook';
import {setIsViewerMenuExposed} from '../state/viewerMenuSlice';
import {HtmlInfo} from '../type/HtmlInfo.interface';
import {TextViewerCoreOption} from '../type/TextViewerCoreOption.type';
import {TextViewerReadType} from '../type/TextViewerReadType';
import {checkShouldRedraw} from '../util/checkShouldRedraw';

const _HtmlScrollViewer: ForwardRefRenderFunction<
  HTMLDivElement,
  {
    htmlInfo: HtmlInfo;
    option: TextViewerCoreOption;
    productId: number;
    wrapperRef?: RefObject<HTMLDivElement>;
  }
> = ({htmlInfo, option, productId, wrapperRef}, forwardedRef) => {
  const [containerElement, setContainerElement] =
    useState<HTMLDivElement | null>(null);
  const {isMobile} = requestContext.requestInfo.display;

  const windowEventBlockerRef = useWindowEventBlockerRef('click');

  const dispatch = useTypedDispatch();

  const onClickImage = useCallback(
    (element: HTMLImageElement | SVGSVGElement) => {
      dispatch(setIsViewerMenuExposed({isExposed: false}));
      windowEventBlockerRef.current = true;

      if (element instanceof HTMLImageElement) {
        const src = getDataset(element, 'src') || element.src;
        if (src) {
          showViewerImagePopup({
            src,
            isMobile,
          });
        }
      } else if (element instanceof SVGSVGElement) {
        const svgHtml = element.outerHTML;
        showViewerImagePopup({
          html: svgHtml,
          isMobile,
        });
      }
    },
    [dispatch, isMobile, windowEventBlockerRef]
  );

  // contentElement가 비어있거나 htmlInfo 내용이 이전과 동일하지 않다면,
  // 혹은, settingStyleElement가 비어있거나 옵션이 변경됐다면,
  // 컨텐츠의 html, style을 DOM에 덮어씁니다.

  const prevHtmlInfo = usePrevious(htmlInfo);
  const prevOption = usePrevious(option);

  const {frameElementsRef, redrawHtmlInfo} = useDrawHtmlInfoHandler({
    productId,
    readType: TextViewerReadType.Scroll,
    containerElement,
    wrapperElement: wrapperRef?.current,
    htmlInfo,
    prevHtmlInfo,
    option,
    onClickImage,
  });

  useEffect(() => {
    if (!containerElement || !option.size.width || !option.size.height) {
      return;
    }

    const shouldRedraw = checkShouldRedraw({
      frameElements: frameElementsRef.current,
      htmlInfo,
      prevHtmlInfo,
      option,
      prevOption,
      isPreview: false,
    });

    if (shouldRedraw) {
      redrawHtmlInfo();
    }
  }, [
    containerElement,
    frameElementsRef,
    htmlInfo,
    option,
    prevHtmlInfo,
    prevOption,
    redrawHtmlInfo,
  ]);

  return (
    <div
      ref={forwardedRef}
      className={twMerge('min-h-screen', !isMobile && 'py-[10vh]')}
    >
      <div ref={setContainerElement} className="min-h-full"></div>
    </div>
  );
};

export const HtmlScrollViewer = forwardRef(_HtmlScrollViewer);
